import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";

const API = API_BASE;
const opts = { withCredentials: true };

export function useAdminData(user) {
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [paymentMode, setPaymentMode] = useState(null);
  const [error, setError] = useState("");
  const [dataError, setDataError] = useState("");

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/api/leads`, opts)
      .then((res) => setLeads(res.data.leads))
      .catch((e) => setError(e.response?.data?.detail || "Chargement des demandes impossible."));
    axios.get(`${API}/api/payments/history`, opts).then((res) => setPayments(res.data.payments)).catch(() => {});
    axios.get(`${API}/api/payments/subscriptions`, opts).then((res) => setSubs(res.data.subscriptions)).catch(() => {});
    axios.get(`${API}/api/maintenance/requests`, opts).then((res) => setRequests(res.data.requests)).catch(() => {});
    axios.get(`${API}/api/payments/mode`, opts).then((res) => setPaymentMode(res.data.mode)).catch(() => {});
  }, [user]);

  const reset = () => {
    setLeads([]);
    setPayments([]);
    setSubs([]);
    setRequests([]);
    setPaymentMode(null);
    setError("");
    setDataError("");
  };

  const cancelSub = async (subId) => {
    if (!window.confirm("Résilier cet abonnement ? Il restera actif jusqu'à la fin de la période payée.")) return;
    setDataError("");
    try {
      await axios.post(`${API}/api/payments/cancel-subscription`, { subscription_id: subId }, opts);
      setSubs((list) => list.map((s) => (s.id === subId ? { ...s, cancel_at_period_end: true } : s)));
    } catch {
      setDataError("Résiliation impossible. Réessayez.");
    }
  };

  const resolveRequest = async (id) => {
    setDataError("");
    try {
      await axios.post(`${API}/api/maintenance/requests/${id}/resolve`, {}, opts);
      setRequests((list) => list.map((r) => (r.id === id ? { ...r, status: "traitee" } : r)));
    } catch {
      setDataError("Mise à jour impossible. Réessayez.");
    }
  };

  const followUpLead = async (id) => {
    setDataError("");
    try {
      const { data } = await axios.post(`${API}/api/leads/${id}/follow-up`, {}, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, follow_up_sent_at: data.follow_up_sent_at, follow_up_count: (l.follow_up_count || 0) + 1 } : l)));
      return true;
    } catch (e) {
      setDataError(e.response?.data?.detail || "Relance impossible. Réessayez.");
      return false;
    }
  };

  const setLeadStatus = async (id, status) => {
    setDataError("");
    try {
      await axios.patch(`${API}/api/leads/${id}/status`, { status }, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      setDataError("Changement de statut impossible. Réessayez.");
    }
  };

  const analyzeLead = async (id) => {
    setDataError("");
    try {
      const { data } = await axios.post(`${API}/api/leads/${id}/analyze`, {}, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, ai: data.ai, ai_status: "done" } : l)));
    } catch (e) {
      setDataError(e.response?.data?.detail || "Analyse IA impossible. Réessayez.");
    }
  };

  const saveBrief = async (id, brief) => {
    setDataError("");
    try {
      await axios.patch(`${API}/api/leads/${id}/brief`, { brief }, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, ai: { ...l.ai, brief, edited_at: new Date().toISOString() } } : l)));
      return true;
    } catch (e) {
      setDataError(e.response?.data?.detail || "Enregistrement du brief impossible. Réessayez.");
      return false;
    }
  };

  const deliverSite = async (id, siteUrl) => {
    setDataError("");
    try {
      const { data } = await axios.post(`${API}/api/leads/${id}/deliver`, { site_url: siteUrl }, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, site_url: data.site_url, delivered_at: data.delivered_at, status: "publie" } : l)));
      return true;
    } catch (e) {
      setDataError(e.response?.data?.detail || "Envoi de la livraison impossible. Réessayez.");
      return false;
    }
  };

  const sendPreview = async (id, previewUrl) => {
    setDataError("");
    try {
      const { data } = await axios.post(`${API}/api/leads/${id}/preview`, { preview_url: previewUrl }, opts);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, preview_url: data.preview_url, preview_sent_at: data.preview_sent_at, preview_status: "pending", status: data.status } : l)));
      return true;
    } catch (e) {
      setDataError(e.response?.data?.detail || "Envoi de la maquette impossible. Réessayez.");
      return false;
    }
  };

  return { leads, payments, subs, requests, paymentMode, error, dataError, reset, cancelSub, resolveRequest, followUpLead, setLeadStatus, analyzeLead, saveBrief, deliverSite, sendPreview };
}
